/** App entry point — route tree, providers, and layout composition. */
import { lazy, Suspense } from 'react';
import { Routes, Route, Outlet } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import LoadingIcon from '@/foundation/ui/LoadingIcon';

import { AuthProvider } from '@/shared/auth/AuthProvider';
import { ImpersonationBanner } from '@/shared/auth/ImpersonationBanner';
import { ThemeProvider } from '@/shared/hooks/useTheme';
import { RequireAuth } from '@/shared/auth/RequireAuth';
import { RequireStudio } from '@/shared/auth/RequireStudio';
import { RequireLearnerOnboarding } from '@/shared/auth/RequireLearnerOnboarding';
import { SessionGuardProvider } from '@/shared/hooks/useSessionGuard';
import { usePersonalLessonToasts } from '@/shared/hooks/usePersonalLessonToasts';
import { LearnerBoundary } from '@/interfaces/learner/boundary/LearnerBoundary';
import LearnerShell from '@/interfaces/learner/shell/LearnerShell';
import { SessionPlayerBoundary } from '@/interfaces/session-player/boundary/SessionPlayerBoundary';
import SessionPlayerShell from '@/interfaces/session-player/shell/SessionPlayerShell';

import { queryClient } from './queryClient';
import ScrollToTop from '@/shared/components/ScrollToTop';
import RetinaCustomCursor from '@/shared/components/RetinaCustomCursor';

import { LearnerRoutes } from '@/features/learner';
import { RequireAdminArea } from '@/shared/auth/RequireAdminArea';
import ReviseSessionPage from '@/features/session-player/ReviseSessionPage';
import LessonSessionPage from '@/features/session-player/LessonSessionPage';
import PersonalLessonPage from '@/features/session-player/PersonalLessonPage';
import WelcomePage from '@/features/learner/WelcomePage';
import LoginPage from '@/shared/auth/LoginPage';
import SignUpPage from '@/shared/auth/SignUpPage';
import EmailSentPage from '@/shared/auth/EmailSentPage';
import EmailVerifiedPage from '@/shared/auth/EmailVerifiedPage';
import ForgotPasswordPage from '@/shared/auth/ForgotPasswordPage';
import ResetPasswordPage from '@/shared/auth/ResetPasswordPage';
import AcceptInvitationPage from '@/shared/auth/AcceptInvitationPage';

/** Lazy-load the entire Studio feature (shell + routes) as one chunk. */
const LazyStudioEntry = lazy(() => import('@/features/studio/StudioEntry'));

/** Loading indicator for lazy-route Suspense boundaries. */
function RouteFallback() {
  return (
    <div
      className="flex min-h-dvh items-start justify-center pt-[28vh]"
      style={{ background: 'var(--color-bg)' }}
    >
      <LoadingIcon size="lg" />
    </div>
  );
}

/** Lazy-load the entire Admin feature (shell + routes) as one chunk. */
const LazyAdminEntry = lazy(() => import('@/features/admin/AdminEntry'));

import '@fontsource-variable/plus-jakarta-sans';
import '@fontsource-variable/instrument-sans';

/** Layout route that wraps children in the Shell (header + footer). */
function LearnerShellLayout() {
  return (
    <LearnerBoundary>
      <LearnerShell>
        <ScrollToTop />
        <Outlet />
      </LearnerShell>
    </LearnerBoundary>
  );
}

function SessionPlayerLayout() {
  return (
    <SessionPlayerBoundary>
      <SessionPlayerShell>
        <Outlet />
      </SessionPlayerShell>
    </SessionPlayerBoundary>
  );
}

/** Invisible component that drives personal lesson generation toasts. */
function PersonalLessonToastManager() {
  usePersonalLessonToasts();
  return null;
}

/** Layout route that provides the session abandon guard overlay. */
function SessionGuardLayout() {
  return (
    <SessionGuardProvider>
      <Outlet />
      <PersonalLessonToastManager />
    </SessionGuardProvider>
  );
}

export default function App() {
  return (
    <>
      <RetinaCustomCursor />
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ThemeProvider>
            {/* Sticky banner at the top of every screen when the current
                session is an admin impersonation. Returns null otherwise. */}
            <ImpersonationBanner />
            <Routes>
              {/* Public routes — no auth required */}
              <Route
                path="login"
                element={
                  <LearnerBoundary>
                    <LoginPage />
                  </LearnerBoundary>
                }
              />
              <Route
                path="sign-up"
                element={
                  <LearnerBoundary>
                    <SignUpPage />
                  </LearnerBoundary>
                }
              />
              <Route
                path="email-sent"
                element={
                  <LearnerBoundary>
                    <EmailSentPage />
                  </LearnerBoundary>
                }
              />
              <Route
                path="email-verified"
                element={
                  <LearnerBoundary>
                    <EmailVerifiedPage />
                  </LearnerBoundary>
                }
              />
              <Route
                path="forgot-password"
                element={
                  <LearnerBoundary>
                    <ForgotPasswordPage />
                  </LearnerBoundary>
                }
              />
              <Route
                path="reset-password"
                element={
                  <LearnerBoundary>
                    <ResetPasswordPage />
                  </LearnerBoundary>
                }
              />
              <Route
                path="accept-invitation"
                element={
                  <LearnerBoundary>
                    <AcceptInvitationPage />
                  </LearnerBoundary>
                }
              />

              <Route element={<RequireAuth />}>
                <Route element={<RequireLearnerOnboarding />}>
                  {/* Welcome page is inside the onboarding guard so already-
                      onboarded users get redirected back to / instead of
                      seeing the welcome screen. RequireLearnerOnboarding's
                      conditions are mutually exclusive (redirect TO /welcome
                      if not onboarded, redirect AWAY from /welcome if
                      onboarded), so there's no loop. */}
                  <Route
                    path="welcome"
                    element={
                      <LearnerBoundary>
                        <WelcomePage />
                      </LearnerBoundary>
                    }
                  />
                  <Route element={<SessionGuardLayout />}>
                    {/* Full-viewport routes (no shell) */}
                    <Route element={<SessionPlayerLayout />}>
                      <Route path="revision/session" element={<ReviseSessionPage />} />
                      <Route path="courses/:courseId/:lessonId" element={<LessonSessionPage />} />
                      <Route
                        path="lessons/:folderSlug/:lessonId"
                        element={<PersonalLessonPage />}
                      />
                    </Route>

                    {/* Studio — lazy-loaded with ceremony loading screen inside StudioEntry */}
                    <Route
                      path="studio/*"
                      element={
                        <RequireStudio>
                          <Suspense fallback={<RouteFallback />}>
                            <LazyStudioEntry />
                          </Suspense>
                        </RequireStudio>
                      }
                    />

                    {/* Admin — lazy-loaded with own shell, full viewport */}
                    <Route
                      path="admin/*"
                      element={
                        <RequireAdminArea>
                          <Suspense fallback={<RouteFallback />}>
                            <LazyAdminEntry />
                          </Suspense>
                        </RequireAdminArea>
                      }
                    />

                    {/* Learner shell (header layout) */}
                    <Route element={<LearnerShellLayout />}>
                      <Route path="*" element={<LearnerRoutes />} />
                    </Route>
                  </Route>
                </Route>
              </Route>
            </Routes>
            <Toaster position="bottom-right" />
          </ThemeProvider>
        </AuthProvider>
      </QueryClientProvider>
    </>
  );
}
